import { useState } from "react";
import { Sparkles, X, Gem, CreditCard, Landmark, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { daysLeft } from "../lib/license";
import { inTrial, startBaseCheckout, startDentalCheckout, startProCheckout } from "../lib/premium";
import { openTransfer } from "../lib/transfer";

const PLANS = [
  { id: "base", name: "Básico", price: "150", color: "blue", checkout: startBaseCheckout,
    features: ["Expediente clínico completo", "Recetas y PDF con QR", "Somatometría y agenda"] },
  { id: "dental", name: "Odontológico", price: "170", color: "teal", checkout: startDentalCheckout,
    features: ["Todo lo del Básico", "Odontograma interactivo", "Periodoncia y ortodoncia"] },
  { id: "pro", name: "Pro", price: "400", color: "terracotta", checkout: startProCheckout, popular: true,
    features: ["Todo lo del Básico", "Dictado con IA (Voice)", "Equipo y accesos delegados"] },
];

const RING = { blue: "border-blue-500", teal: "border-teal-500", terracotta: "border-brand-terracotta" };
const BTN = { blue: "bg-blue-500 hover:bg-blue-600", teal: "bg-teal-500 hover:bg-teal-600", terracotta: "bg-brand-terracotta hover:bg-brand-terracottaDark" };

export default function TrialBanner() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem("clinped_trial_banner_hidden") === new Date().toDateString());

  if (!inTrial(user)) return null;
  const dl = daysLeft(user);
  if (dl === null) return null;
  if (hidden) return null;

  const urgent = dl <= 3;
  const dismiss = () => { localStorage.setItem("clinped_trial_banner_hidden", new Date().toDateString()); setHidden(true); };

  return (
    <>
      <div
        data-testid="trial-banner"
        className={`flex items-center justify-between gap-3 px-4 sm:px-5 py-2.5 ${urgent ? "bg-amber-500" : "bg-gradient-to-r from-brand-sage to-brand-sageDark"} text-white`}>
        <div className="flex items-center gap-2.5 min-w-0">
          <Sparkles size={17} className="shrink-0" />
          <p className="text-sm font-nunito font-semibold truncate">
            {dl > 0 ? <>Te quedan <b>{dl} {dl === 1 ? "día" : "días"}</b> de prueba gratis</> : <>Hoy termina tu prueba gratis</>}
            <span className="hidden sm:inline"> — elige tu plan para no perder el acceso.</span>
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid="trial-choose-plan-btn" onClick={() => setOpen(true)}
            className="bg-white/95 hover:bg-white text-brand-ink font-outfit font-bold text-xs sm:text-sm px-3.5 py-1.5 rounded-xl transition-all">
            Elegir mi plan
          </button>
          <button data-testid="trial-banner-dismiss" onClick={dismiss} className="p-1.5 rounded-lg hover:bg-white/20 transition-all" title="Ocultar por hoy">
            <X size={16} />
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="trial-plans-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-brand-ink flex items-center gap-2"><Gem size={20} className="text-brand-terracotta" /> Elige tu plan</DialogTitle>
            <DialogDescription>Continúa disfrutando de ClinPed. Paga con tarjeta o por transferencia.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-3 gap-4 mt-1">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={`trial-plan-${p.id}`}
                className={`relative rounded-3xl border-2 ${RING[p.color]} bg-white p-5 flex flex-col ${p.popular ? "shadow-xl" : "shadow-sm"}`}>
                {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide text-white px-2.5 py-0.5 rounded-full bg-brand-terracotta">Más popular</span>}
                <p className="font-outfit font-bold text-brand-ink text-lg">{p.name}</p>
                <p className="font-outfit font-extrabold text-3xl text-brand-ink mt-1">${p.price}<span className="text-sm font-semibold"> MXN/mes</span></p>
                <ul className="space-y-1.5 mt-3 mb-4 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-brand-ink/75"><CheckCircle2 size={14} className="text-brand-sage shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <button data-testid={`trial-pay-card-${p.id}`} onClick={p.checkout}
                  className={`w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold text-sm py-2.5 rounded-2xl transition-all ${BTN[p.color]}`}>
                  <CreditCard size={15} /> Pagar con tarjeta
                </button>
                <button data-testid={`trial-pay-transfer-${p.id}`} onClick={() => { setOpen(false); openTransfer(p.id); }}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-brand-sageDark hover:underline">
                  <Landmark size={13} /> Pagar por transferencia
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
